import { useEffect, useRef, useState } from 'react';
import { IconExternalLink, IconFileText, IconTrash, IconX } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { Segmented } from '../ui/prims';

/**
 * Target picker for a link region.
 *
 * A link with no target exports nothing at all, so drawing one has to ask for
 * the destination immediately rather than leaving an invisible dead rectangle
 * on the page.
 */
export default function LinkModal({ object, pageCount, isNew, onApply, onRemove, onClose }) {
    const { t } = useTranslation();
    const [mode, setMode] = useState(Number.isInteger(object?.targetPage) ? 'page' : 'url');
    const [url, setUrl] = useState(object?.url || '');
    const [targetPage, setTargetPage] = useState(
        Number.isInteger(object?.targetPage) ? object.targetPage + 1 : 1,
    );
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, [mode]);

    const valid = mode === 'url'
        ? url.trim().length > 2
        : targetPage >= 1 && targetPage <= pageCount;

    const apply = () => {
        if (!valid) return;
        onApply(mode === 'url'
            ? { url: url.trim(), targetPage: null }
            : { url: '', targetPage: targetPage - 1 });
        onClose();
    };

    const field = 'h-10 w-full rounded-xl border border-[#ECE8E4] bg-white px-3 text-[13.5px] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

    return (
        <div className="fixed inset-0 z-[9100] flex items-center justify-center bg-[#1C1815]/45 p-4">
            <div className="w-full max-w-[440px] overflow-hidden rounded-2xl border border-[#ECE8E4] bg-white shadow-[0_24px_64px_rgba(28,24,21,0.28)] dark:border-zinc-700 dark:bg-zinc-900">
                <div className="flex h-14 items-center justify-between border-b border-[#ECE8E4] px-4 dark:border-zinc-800">
                    <h2 className="text-[15px] font-semibold text-[#1C1815] dark:text-zinc-100">
                        {t('pdfEditor.link.title')}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B635C] hover:bg-[#F4F1ED] dark:text-zinc-400 dark:hover:bg-zinc-800"
                    >
                        <IconX size={18} stroke={1.8} />
                    </button>
                </div>

                <div className="p-4">
                    <div className="mb-3">
                        <Segmented
                            value={mode}
                            onChange={setMode}
                            options={[
                                { value: 'url', label: t('pdfEditor.link.web'), icon: IconExternalLink },
                                { value: 'page', label: t('pdfEditor.link.page'), icon: IconFileText },
                            ]}
                        />
                    </div>

                    {mode === 'url' ? (
                        <input
                            ref={inputRef}
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') apply();
                                e.stopPropagation();
                            }}
                            placeholder="https://example.com"
                            className={field}
                        />
                    ) : (
                        <div className="flex items-center gap-2">
                            <input
                                ref={inputRef}
                                type="number"
                                min={1}
                                max={pageCount}
                                value={targetPage}
                                onChange={(e) => setTargetPage(Number(e.target.value))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') apply();
                                    e.stopPropagation();
                                }}
                                className={`${field} w-24`}
                            />
                            <span className="text-[12.5px] text-[#6B635C] dark:text-zinc-400">
                                {t('pdfEditor.link.ofPages', { count: toBnDigits(pageCount) })}
                            </span>
                        </div>
                    )}

                    <p className="mt-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                        {t('pdfEditor.link.hint')}
                    </p>
                </div>

                <div className="flex h-14 items-center justify-between gap-2 border-t border-[#ECE8E4] px-4 dark:border-zinc-800">
                    <button
                        type="button"
                        onClick={() => { onRemove(); onClose(); }}
                        className="flex h-9 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium text-[#D33A2C] hover:bg-[#FDECEA] dark:hover:bg-red-950/30"
                    >
                        <IconTrash size={15} stroke={1.8} />
                        {t('pdfEditor.link.remove')}
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => { if (isNew) onRemove(); onClose(); }}
                            className="flex h-9 items-center rounded-lg border border-[#ECE8E4] px-4 text-[13px] font-medium text-[#3D3833] dark:border-zinc-700 dark:text-zinc-200"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={apply}
                            disabled={!valid}
                            className="flex h-9 items-center rounded-lg bg-primary px-4 text-[13px] font-semibold text-white hover:bg-primary-700 disabled:opacity-45"
                        >
                            {t('pdfEditor.link.apply')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
